import { includes, isInteger, isString, toInteger } from 'lodash'
import queryString from 'query-string'

import { IRange } from 'main/util/range'

const unboundedRangeValue = 'any'

export const budgetFilterRange = {
  max: 400000,
  min: 0,
}

export const weeklyBudgetFilterRange = {
  max: budgetFilterRange.max / 4,
  min: 0,
}

const PRE = 'MAIN-UTIL-AGE-RANGE'
// each step of the slider represents a value that doesn't increase linearly (1 month steps -> 0.5 year steps > 1 year steps)
// HACK (mark e): 6 weeks would be something like 1.4 months, but our ages are stored as integers
export const ageRangeLabelsAndValues = [
  { label: 'Any Age', value: null, key: `${PRE}-ANY`, qty: null },
  { label: '6 weeks', value: 1, key: `${PRE}-WEEKS`, qty: null },
  { label: '2 months', value: 2, key: `${PRE}-MONTHS`, qty: '2' },
  { label: '3 months', value: 3, key: `${PRE}-MONTHS`, qty: '3' },
  { label: '4 months', value: 4, key: `${PRE}-MONTHS`, qty: '4' },
  { label: '5 months', value: 5, key: `${PRE}-MONTHS`, qty: '5' },
  { label: '6 months', value: 6, key: `${PRE}-MONTHS`, qty: '6' },
  { label: '7 months', value: 7, key: `${PRE}-MONTHS`, qty: '7' },
  { label: '8 months', value: 8, key: `${PRE}-MONTHS`, qty: '8' },
  { label: '9 months', value: 9, key: `${PRE}-MONTHS`, qty: '9' },
  { label: '10 months', value: 10, key: `${PRE}-MONTHS`, qty: '10' },
  { label: '11 months', value: 11, key: `${PRE}-MONTHS`, qty: '11' },
  { label: '12 months', value: 12, key: `${PRE}-MONTHS`, qty: '12' },
  { label: '13 months', value: 13, key: `${PRE}-MONTHS`, qty: '13' },
  { label: '14 months', value: 14, key: `${PRE}-MONTHS`, qty: '14' },
  { label: '15 months', value: 15, key: `${PRE}-MONTHS`, qty: '15' },
  { label: '16 months', value: 16, key: `${PRE}-MONTHS`, qty: '16' },
  { label: '17 months', value: 17, key: `${PRE}-MONTHS`, qty: '17' },
  { label: '18 months', value: 18, key: `${PRE}-MONTHS`, qty: '18' },
  { label: '19 months', value: 19, key: `${PRE}-MONTHS`, qty: '19' },
  { label: '20 months', value: 20, key: `${PRE}-MONTHS`, qty: '20' },
  { label: '21 months', value: 21, key: `${PRE}-MONTHS`, qty: '21' },
  { label: '22 months', value: 22, key: `${PRE}-MONTHS`, qty: '22' },
  { label: '23 months', value: 23, key: `${PRE}-MONTHS`, qty: '23' },
  { label: '24 months', value: 24, key: `${PRE}-MONTHS`, qty: '24' },
  { label: '2.5 years', value: 30, key: `${PRE}-YEARS`, qty: '2.5' },
  { label: '3 years', value: 36, key: `${PRE}-YEARS`, qty: '3' },
  { label: '3.5 years', value: 42, key: `${PRE}-YEARS`, qty: '3.5' },
  { label: '4 years', value: 48, key: `${PRE}-YEARS`, qty: '4' },
  { label: '4.5 years', value: 54, key: `${PRE}-YEARS`, qty: '4.5' },
  { label: '5 years', value: 60, key: `${PRE}-YEARS`, qty: '5' },
  { label: '6 years', value: 72, key: `${PRE}-YEARS`, qty: '6' },
  { label: '7 years', value: 84, key: `${PRE}-YEARS`, qty: '7' },
  { label: '8 years', value: 96, key: `${PRE}-YEARS`, qty: '8' },
  { label: '9 years', value: 108, key: `${PRE}-YEARS`, qty: '9' },
  { label: '10 years', value: 120, key: `${PRE}-YEARS`, qty: '10' },
  { label: '11 years', value: 132, key: `${PRE}-YEARS`, qty: '11' },
  { label: '12 years', value: 144, key: `${PRE}-YEARS`, qty: '12' },
  { label: '13 years', value: 156, key: `${PRE}-YEARS`, qty: '13' },
  { label: '14 years', value: 168, key: `${PRE}-YEARS`, qty: '14' },
  { label: '15 years', value: 180, key: `${PRE}-YEARS`, qty: '15' },
  { label: '16 years', value: 192, key: `${PRE}-YEARS`, qty: '16' },
  { label: '17 years', value: 204, key: `${PRE}-YEARS`, qty: '17' },
  { label: 'Any Age', value: null, key: `${PRE}-ANY`, qty: null },
]

export interface IFilters {
  acceptsSubsidy: boolean
  acceptsWeeklyBilling: boolean
  budget: IRange<number | null>
  daysPerWeek: number[]
  dropOffEndsAt: string | null
  dropOffStartsAt: string | null
  hasAvailability: boolean | null
  hasUpcomingGatherings: boolean
  isTouring: boolean
  pickUpEndsAt: string | null
  pickUpStartsAt: string | null
  selectedProgramOfferings: number[]
  selectedProgramTypes: string[]
  selectedNetworkRatings: number[]
  selectedNetworkRatingsV2: string[]
  selectedSpokenLanguages: string[]
  enrollingAgeMonthsMin: number | null
  enrollingAgeMonthsMax: number | null
  selectedSubsidies: number[]
}

enum FilterQueryKey {
  ACCEPTS_SUBSIDY = 'subsidy',
  ACCEPTS_WEEKLY_BILLING = 'weekly',
  BUDGET = 'budget',
  DAYS_PER_WEEK = 'days',
  DROP_OFF_ENDS_AT = 'drop-off-end',
  DROP_OFF_STARTS_AT = 'drop-off-start',
  HAS_AVAILABILITY = 'availability',
  HAS_UPCOMING_GATHERINGS = 'events',
  IS_OPEN_COVID19 = 'open-covid19',
  IS_TOURING = 'touring',
  PICK_UP_ENDS_AT = 'pick_up-end',
  PICK_UP_STARTS_AT = 'pick_up-start',
  PROGRAM_OFFERINGS = 'program-offerings',
  PROGRAM_TYPE = 'program-type',
  NETWORK_RATINGS = 'network-ratings',
  NETWORK_RATINGS_V2 = 'network-ratings-v2',
  SPOKEN_LANGUAGES = 'spoken-languages',
  ENROLLING_AGE_MONTHS_MIN = 'min-age',
  ENROLLING_AGE_MONTHS_MAX = 'max-age',
  SELECTED_SUBSIDIES = 'subsidies',
}

type QueryParam = string | string[] | null | undefined

const queryKeyManifest: { [key in keyof IFilters]: FilterQueryKey } = {
  acceptsSubsidy: FilterQueryKey.ACCEPTS_SUBSIDY,
  acceptsWeeklyBilling: FilterQueryKey.ACCEPTS_WEEKLY_BILLING,
  budget: FilterQueryKey.BUDGET,
  daysPerWeek: FilterQueryKey.DAYS_PER_WEEK,
  dropOffEndsAt: FilterQueryKey.DROP_OFF_ENDS_AT,
  dropOffStartsAt: FilterQueryKey.DROP_OFF_STARTS_AT,
  hasUpcomingGatherings: FilterQueryKey.HAS_UPCOMING_GATHERINGS,
  isTouring: FilterQueryKey.IS_TOURING,
  pickUpEndsAt: FilterQueryKey.PICK_UP_ENDS_AT,
  pickUpStartsAt: FilterQueryKey.PICK_UP_STARTS_AT,
  selectedProgramOfferings: FilterQueryKey.PROGRAM_OFFERINGS,
  selectedProgramTypes: FilterQueryKey.PROGRAM_TYPE,
  hasAvailability: FilterQueryKey.HAS_AVAILABILITY,
  selectedNetworkRatings: FilterQueryKey.NETWORK_RATINGS,
  selectedNetworkRatingsV2: FilterQueryKey.NETWORK_RATINGS_V2,
  selectedSpokenLanguages: FilterQueryKey.SPOKEN_LANGUAGES,
  enrollingAgeMonthsMin: FilterQueryKey.ENROLLING_AGE_MONTHS_MIN,
  enrollingAgeMonthsMax: FilterQueryKey.ENROLLING_AGE_MONTHS_MAX,
  selectedSubsidies: FilterQueryKey.SELECTED_SUBSIDIES,
}

export function initializeFilters(): IFilters {
  return {
    acceptsSubsidy: false,
    acceptsWeeklyBilling: false,
    budget: { min: null, max: null },
    daysPerWeek: [],
    dropOffEndsAt: null,
    dropOffStartsAt: null,
    hasAvailability: false,
    hasUpcomingGatherings: false,
    isTouring: false,
    pickUpEndsAt: null,
    pickUpStartsAt: null,
    selectedProgramOfferings: [],
    selectedProgramTypes: [],
    selectedNetworkRatings: [],
    selectedNetworkRatingsV2: [],
    selectedSpokenLanguages: [],
    enrollingAgeMonthsMin: null,
    enrollingAgeMonthsMax: null,
    selectedSubsidies: [],
  }
}

export function numberOfActiveFilters(filters: IFilters) {
  let counter: number = 0

  if (filters.acceptsSubsidy) counter += 1
  if (filters.selectedSubsidies.length) counter += 1
  if (filters.acceptsWeeklyBilling) counter += 1
  if (filters.budget.min !== null || filters.budget.max !== null) counter += 1
  if (filters.dropOffEndsAt || filters.dropOffStartsAt) counter += 1
  if (filters.daysPerWeek.length) counter += 1
  if (filters.hasAvailability) counter += 1
  if (filters.hasUpcomingGatherings) counter += 1
  if (filters.isTouring) counter += 1
  if (filters.pickUpStartsAt || filters.pickUpEndsAt) counter += 1
  if (filters.selectedProgramOfferings.length) counter += 1
  if (filters.selectedProgramTypes.length) counter += 1
  if (filters.selectedNetworkRatings.length) counter += 1
  if (filters.selectedNetworkRatingsV2.length) counter += 1
  if (filters.selectedSpokenLanguages.length) counter += 1
  if (filters.enrollingAgeMonthsMin || filters.enrollingAgeMonthsMax) counter += 1

  return counter
}

export interface IAllowableFilterOptions {
  selectedNetworkRatingsV2?: string[] | undefined
  enrollingAgeMonthsMin?: number | null
  enrollingAgeMonthsMax?: number | null
}

export function buildFiltersFromQuery(
  urlQuery: string,
  allowableOptions?: IAllowableFilterOptions,
) {
  const params = queryString.parse(urlQuery, { arrayFormat: 'comma' })
  return buildFiltersFromQueryParams(params, allowableOptions)
}

function buildFiltersFromQueryParams(
  params: queryString.ParsedQuery<string>,
  allowableOptions?: IAllowableFilterOptions,
) {
  const filters = initializeFilters()

  if (queryKeyManifest.acceptsSubsidy in params) filters.acceptsSubsidy = true
  if (queryKeyManifest.acceptsWeeklyBilling in params) filters.acceptsWeeklyBilling = true
  if (queryKeyManifest.budget in params) {
    const { min, max } = parseParamAsUnboundedIntRange(params[queryKeyManifest.budget])

    filters.budget = {
      max: max && max * 100,
      min: min && min * 100,
    }
  }
  if (queryKeyManifest.daysPerWeek in params) {
    const daysPerWeek = params[queryKeyManifest.daysPerWeek]
    if (Array.isArray(daysPerWeek)) {
      filters.daysPerWeek = daysPerWeek.map(toInteger).filter(n => n >= 1 && n <= 7)
    } else if (isString(daysPerWeek)) {
      const maybeDays = toInteger(daysPerWeek)
      filters.daysPerWeek =
        isInteger(maybeDays) && maybeDays >= 1 && maybeDays <= 7 ? [maybeDays] : []
    }
  }
  if (queryKeyManifest.dropOffEndsAt in params) {
    filters.dropOffEndsAt = getValidateTimeParam(params[queryKeyManifest.dropOffEndsAt])
  }
  if (queryKeyManifest.dropOffStartsAt in params) {
    filters.dropOffStartsAt = getValidateTimeParam(params[queryKeyManifest.dropOffStartsAt])
  }

  if (queryKeyManifest.enrollingAgeMonthsMin in params) {
    const param = params[queryKeyManifest.enrollingAgeMonthsMin]
    if (typeof param === 'string') {
      const enrollingAgeMonthsMin = parseInt(param, 10)
      if (enrollingAgeMonthsMin >= 0) filters.enrollingAgeMonthsMin = enrollingAgeMonthsMin
    }
  }

  if (queryKeyManifest.enrollingAgeMonthsMax in params) {
    const param = params[queryKeyManifest.enrollingAgeMonthsMax]
    if (typeof param === 'string') {
      const enrollingAgeMonthsMax = parseInt(param, 10)
      if (enrollingAgeMonthsMax >= 0) filters.enrollingAgeMonthsMax = enrollingAgeMonthsMax
    }
  }

  if (queryKeyManifest.hasUpcomingGatherings in params) filters.hasUpcomingGatherings = true
  if (queryKeyManifest.isTouring in params) filters.isTouring = true
  if (queryKeyManifest.pickUpEndsAt in params) {
    filters.pickUpEndsAt = getValidateTimeParam(params[queryKeyManifest.pickUpEndsAt])
  }
  if (queryKeyManifest.pickUpStartsAt in params) {
    filters.pickUpStartsAt = getValidateTimeParam(params[queryKeyManifest.pickUpStartsAt])
  }
  if (queryKeyManifest.selectedProgramTypes in params) {
    const selectedProgramTypes = params[queryKeyManifest.selectedProgramTypes]
    const availableProgramTypes = [
      'fcc',
      'center',
      'forest',
      'micro',
      'nanny',
      'ministry',
      'other',
      'exempt',
    ]
    if (Array.isArray(selectedProgramTypes)) {
      filters.selectedProgramTypes = selectedProgramTypes.filter(pt =>
        includes(availableProgramTypes, pt),
      )
    } else {
      const maybeProgram: any = selectedProgramTypes
      filters.selectedProgramTypes = includes(availableProgramTypes, maybeProgram)
        ? [maybeProgram]
        : []
    }
  }
  if (queryKeyManifest.selectedSpokenLanguages in params) {
    const selectedSpokenLanguages = params[queryKeyManifest.selectedSpokenLanguages]
    const availableSpokenLanguages = ['English', 'Spanish']
    if (Array.isArray(selectedSpokenLanguages)) {
      filters.selectedSpokenLanguages = selectedSpokenLanguages.filter(pt =>
        includes(availableSpokenLanguages, pt),
      )
    } else {
      const maybeSpokenLanguage: any = selectedSpokenLanguages
      filters.selectedSpokenLanguages = includes(availableSpokenLanguages, maybeSpokenLanguage)
        ? [maybeSpokenLanguage]
        : []
    }
  }
  if (queryKeyManifest.selectedSubsidies in params) {
    const selectedSubsidies = params[queryKeyManifest.selectedSubsidies]
    if (Array.isArray(selectedSubsidies)) {
      filters.selectedSubsidies = selectedSubsidies
        .map(value => parseInt(value, 10))
        .filter(value => Number.isInteger(value) && value > 0)
    } else {
      filters.selectedSubsidies =
        typeof selectedSubsidies === 'string'
          ? [parseInt(selectedSubsidies, 10)].filter(value => Number.isInteger(value) && value > 0)
          : []
    }
  }

  if (params[queryKeyManifest.selectedProgramOfferings]) {
    const param = params[queryKeyManifest.selectedProgramOfferings]!
    const selectedProgramOfferings = Array.isArray(param) ? param : [param]
    filters.selectedProgramOfferings = selectedProgramOfferings
      .filter(Boolean)
      .map(v => parseInt(v, 10))
  }

  if (queryKeyManifest.selectedNetworkRatings in params) {
    const selectedNetworkRatings = params[queryKeyManifest.selectedNetworkRatings]
    const availableNetworkRatings = [1, 2, 3, 4, -1]
    if (Array.isArray(selectedNetworkRatings)) {
      filters.selectedNetworkRatings = selectedNetworkRatings
        .map(toInteger)
        .filter(nr => includes(availableNetworkRatings, nr))
    } else {
      const maybeNetworkRating: number = Number(selectedNetworkRatings)
      filters.selectedNetworkRatings = includes(availableNetworkRatings, maybeNetworkRating)
        ? [maybeNetworkRating]
        : []
    }
  }

  if (
    allowableOptions &&
    allowableOptions.selectedNetworkRatingsV2 &&
    queryKeyManifest.selectedNetworkRatingsV2 in params
  ) {
    const selectedNetworkRatings = params[queryKeyManifest.selectedNetworkRatingsV2]

    const availableNetworkRatings = allowableOptions.selectedNetworkRatingsV2

    if (Array.isArray(selectedNetworkRatings)) {
      filters.selectedNetworkRatingsV2 = selectedNetworkRatings.filter(nr =>
        includes(availableNetworkRatings, nr),
      )
    } else {
      filters.selectedNetworkRatingsV2 =
        selectedNetworkRatings && includes(availableNetworkRatings, selectedNetworkRatings)
          ? [selectedNetworkRatings]
          : []
    }
  }

  if (queryKeyManifest.hasAvailability in params) filters.hasAvailability = true

  return filters
}

function parseParamAsUnboundedIntRange(param: QueryParam) {
  const defaultValue = { min: null, max: null }
  if (!Array.isArray(param)) return defaultValue
  if (param.length !== 2) return defaultValue

  const min = param[0] === unboundedRangeValue ? null : parseInt(param[0], 10)
  const max = param[1] === unboundedRangeValue ? null : parseInt(param[1], 10)
  if (min !== null && Number.isNaN(min)) return defaultValue
  if (max !== null && Number.isNaN(max)) return defaultValue

  return { min, max }
}

export function buildQueryParamsFromFilters(filters: IFilters) {
  const params: {
    [key in FilterQueryKey]?: string | number | Array<string | number | null> | null
  } = {}

  if (filters.acceptsSubsidy) params[FilterQueryKey.ACCEPTS_SUBSIDY] = null
  if (filters.acceptsWeeklyBilling) params[FilterQueryKey.ACCEPTS_WEEKLY_BILLING] = null
  if (filters.budget.min !== null || filters.budget.max !== null) {
    params[FilterQueryKey.BUDGET] = [
      filters.budget.min === null ? unboundedRangeValue : filters.budget.min / 100,
      filters.budget.max === null ? unboundedRangeValue : filters.budget.max / 100,
    ]
  }
  if (filters.daysPerWeek.length) {
    params[FilterQueryKey.DAYS_PER_WEEK] = filters.daysPerWeek
  }
  if (filters.dropOffEndsAt) params[FilterQueryKey.DROP_OFF_ENDS_AT] = filters.dropOffEndsAt
  if (filters.dropOffStartsAt) params[FilterQueryKey.DROP_OFF_STARTS_AT] = filters.dropOffStartsAt
  if (filters.enrollingAgeMonthsMin !== null) {
    params[FilterQueryKey.ENROLLING_AGE_MONTHS_MIN] = filters.enrollingAgeMonthsMin
  }
  if (filters.enrollingAgeMonthsMax !== null) {
    params[FilterQueryKey.ENROLLING_AGE_MONTHS_MAX] = filters.enrollingAgeMonthsMax
  }
  if (filters.hasUpcomingGatherings) params[FilterQueryKey.HAS_UPCOMING_GATHERINGS] = null
  if (filters.isTouring) params[FilterQueryKey.IS_TOURING] = null
  if (filters.pickUpEndsAt) params[FilterQueryKey.PICK_UP_ENDS_AT] = filters.pickUpEndsAt
  if (filters.pickUpStartsAt) params[FilterQueryKey.PICK_UP_STARTS_AT] = filters.pickUpStartsAt
  if (filters.selectedProgramTypes.length) {
    params[FilterQueryKey.PROGRAM_TYPE] = filters.selectedProgramTypes
  }
  if (filters.selectedNetworkRatings.length) {
    params[FilterQueryKey.NETWORK_RATINGS] = filters.selectedNetworkRatings
  }

  if (filters.selectedNetworkRatingsV2.length) {
    params[FilterQueryKey.NETWORK_RATINGS_V2] = filters.selectedNetworkRatingsV2
  }
  if (filters.selectedSpokenLanguages.length) {
    params[FilterQueryKey.SPOKEN_LANGUAGES] = filters.selectedSpokenLanguages
  }
  if (filters.selectedSubsidies.length) {
    params[FilterQueryKey.SELECTED_SUBSIDIES] = filters.selectedSubsidies
  }
  if (filters.selectedProgramOfferings.length) {
    params[FilterQueryKey.PROGRAM_OFFERINGS] = filters.selectedProgramOfferings
  }

  if (filters.hasAvailability) params[FilterQueryKey.HAS_AVAILABILITY] = null

  return params
}

function getValidateTimeParam(value: any) {
  const timeFormatRegex = /(?:[01]\d|2[0123]):(?:[012345]\d):(?:[012345]\d)/

  return typeof value === 'string' && typeof value !== 'undefined' && timeFormatRegex.test(value)
    ? value
    : null
}
